import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Menu, X, User, LogOut, LayoutDashboard, Heart, Shield, Guitar, Store, Settings } from "lucide-react";
import { useState } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10 safe-area-top">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
            <img 
              src="/icons/icon-72.png" 
              alt="Rockmaps" 
              className="w-6 h-6"
            />
            <span className="text-xl font-black text-white tracking-tighter group-hover:text-primary transition-colors">
              ROCK<span className="text-primary">MAPS</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/bars"
              className="font-mono text-sm text-zinc-400 hover:text-primary transition-colors"
              data-testid="nav-bars"
            >
              BARES
            </Link>
            <Link
              to="/events"
              className="font-mono text-sm text-zinc-400 hover:text-primary transition-colors"
              data-testid="nav-events"
            >
              EVENTOS
            </Link>
            {user && (
              <Link
                to="/user-dashboard"
                className="font-mono text-sm text-zinc-400 hover:text-primary transition-colors flex items-center gap-1"
                data-testid="nav-my-events"
              >
                <Heart className="w-4 h-4" />
                MIS EVENTOS
              </Link>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="btn-ruido border-2 border-zinc-700 bg-transparent font-mono text-sm"
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {user.name?.split(" ")[0] || "CUENTA"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#111] border-zinc-800 rounded-none">
                  {user.user_type === "admin" && (
                    <DropdownMenuItem 
                      onClick={() => navigate("/admin")}
                      className="font-mono text-sm cursor-pointer hover:bg-primary hover:text-white rounded-none"
                      data-testid="nav-admin"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      ADMIN
                    </DropdownMenuItem>
                  )}
                  {user.user_type === "bar" && (
                    <DropdownMenuItem 
                      onClick={() => navigate("/dashboard")}
                      className="font-mono text-sm cursor-pointer hover:bg-primary hover:text-white rounded-none"
                      data-testid="nav-dashboard"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      MI BAR
                    </DropdownMenuItem>
                  )}
                  {user.user_type === "band" && (
                    <DropdownMenuItem 
                      onClick={() => navigate("/band-dashboard")}
                      className="font-mono text-sm cursor-pointer hover:bg-primary hover:text-white rounded-none"
                      data-testid="nav-band-dashboard"
                    >
                      <Guitar className="w-4 h-4 mr-2" />
                      MI BANDA
                    </DropdownMenuItem>
                  )}
                  {user.user_type === "partner" && (
                    <DropdownMenuItem 
                      onClick={() => navigate("/partner-dashboard")}
                      className="font-mono text-sm cursor-pointer hover:bg-primary hover:text-white rounded-none"
                      data-testid="nav-partner-dashboard"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      PANEL PARTNER
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    onClick={() => navigate("/settings")}
                    className="font-mono text-sm cursor-pointer hover:bg-primary hover:text-white rounded-none"
                    data-testid="nav-settings"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    CONFIGURACIÓN
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="font-mono text-sm cursor-pointer hover:bg-primary hover:text-white rounded-none"
                    data-testid="nav-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    SALIR
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex gap-3 items-center">
                {/* Main User Login with Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="btn-ruido border-2 border-zinc-700 bg-transparent font-mono text-sm"
                      data-testid="nav-login"
                    >
                      ACCEDER
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#111] border-zinc-800 rounded-none min-w-[160px]">
                    <DropdownMenuItem 
                      onClick={() => navigate("/login")}
                      className="font-mono text-sm cursor-pointer hover:bg-primary hover:text-white rounded-none"
                    >
                      <User className="w-4 h-4 mr-2" />
                      USUARIO
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate("/para-bares")}
                      className="font-mono text-sm cursor-pointer hover:bg-primary hover:text-white rounded-none"
                    >
                      <Store className="w-4 h-4 mr-2" />
                      SOY BAR
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate("/login?type=partner")}
                      className="font-mono text-sm cursor-pointer hover:bg-primary hover:text-white rounded-none"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      SOY PARTNER
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-zinc-400 hover:text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-zinc-800">
            <div className="flex flex-col gap-4">
              <Link
                to="/bars"
                onClick={() => setMobileOpen(false)}
                className="font-mono text-sm text-zinc-400 hover:text-primary"
                data-testid="mobile-nav-bars"
              >
                BARES
              </Link>
              <Link
                to="/events"
                onClick={() => setMobileOpen(false)}
                className="font-mono text-sm text-zinc-400 hover:text-primary"
                data-testid="mobile-nav-events"
              >
                EVENTOS
              </Link>
              {user ? (
                <>
                  <Link
                    to="/user-dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="font-mono text-sm text-zinc-400 hover:text-primary flex items-center gap-2"
                    data-testid="mobile-nav-my-events"
                  >
                    <Heart className="w-4 h-4" />
                    MIS EVENTOS
                  </Link>
                  {user.user_type === "admin" && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileOpen(false)}
                      className="font-mono text-sm text-primary hover:text-white flex items-center gap-2"
                      data-testid="mobile-nav-admin"
                    >
                      <Shield className="w-4 h-4" />
                      ADMIN
                    </Link>
                  )}
                  {user.user_type === "bar" && (
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="font-mono text-sm text-zinc-400 hover:text-primary flex items-center gap-2"
                      data-testid="mobile-nav-dashboard"
                    >
                      <Store className="w-4 h-4" />
                      MI BAR
                    </Link>
                  )}
                  {user.user_type === "band" && (
                    <Link
                      to="/band-dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="font-mono text-sm text-zinc-400 hover:text-primary flex items-center gap-2"
                      data-testid="mobile-nav-band-dashboard"
                    >
                      <Guitar className="w-4 h-4" />
                      MI BANDA
                    </Link>
                  )}
                  {user.user_type === "partner" && (
                    <Link
                      to="/partner-dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="font-mono text-sm text-zinc-400 hover:text-primary flex items-center gap-2"
                      data-testid="mobile-nav-partner-dashboard"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      PANEL PARTNER
                    </Link>
                  )}
                  <Link
                    to="/settings"
                    onClick={() => setMobileOpen(false)}
                    className="font-mono text-sm text-zinc-400 hover:text-primary flex items-center gap-2"
                    data-testid="mobile-nav-settings"
                  >
                    <Settings className="w-4 h-4" />
                    CONFIGURACIÓN
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setMobileOpen(false); }}
                    className="font-mono text-sm text-zinc-400 hover:text-primary text-left"
                    data-testid="mobile-nav-logout"
                  >
                    SALIR
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="font-mono text-sm text-white hover:text-primary"
                    data-testid="mobile-nav-login"
                  >
                    ACCEDER USUARIO
                  </Link>
                  
                  <div className="pt-3 mt-3 border-t border-zinc-800">
                    <span className="text-zinc-600 text-xs font-mono block mb-2">ACCESO PROFESIONAL</span>
                    <Link
                      to="/para-bares"
                      onClick={() => setMobileOpen(false)}
                      className="font-mono text-sm text-zinc-500 hover:text-primary flex items-center gap-2 mb-2"
                    >
                      <Store className="w-4 h-4" />
                      SOY BAR
                    </Link>
                    <Link
                      to="/login?type=partner"
                      onClick={() => setMobileOpen(false)}
                      className="font-mono text-sm text-zinc-500 hover:text-primary flex items-center gap-2"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      SOY PARTNER
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
